import React from 'react';
import { twMerge } from 'tailwind-merge';

import { useWebUiGlobalContext } from '../context/global/use-global-context';
import WebUiHeader from './header';
import { MENU, type MenuSection } from './menu-config';
import WebUiStatus from './status';

export default function WebUiLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { currentPage, navigate, logout, currentUser } =
    useWebUiGlobalContext();

  const [sections, setSections] = React.useState<Record<MenuSection, boolean>>({
    status: false,
    settings: false,
    maintenance: false,
  });
  const [hasPoe, setHasPoe] = React.useState(false);

  const toggle = (section: MenuSection) => {
    setSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const isAdmin = currentUser?.userLevel === 0;

  React.useEffect(() => {
    const onPoe = (event: Event) => {
      const customEvent = event as CustomEvent<boolean>;
      setHasPoe(Boolean(customEvent.detail));
    };
    window.addEventListener('webui:poe-available', onPoe as EventListener);
    return () => {
      window.removeEventListener('webui:poe-available', onPoe as EventListener);
    };
  }, []);

  return (
    <div className="relative h-screen w-full bg-webui-bg font-webui text-webui-text">
      <WebUiHeader />
      <WebUiStatus />
      <div className="flex h-full flex-row bg-webui-quinary pt-40">
        <div className="relative flex h-full w-64 flex-col gap-1 overflow-auto px-2 py-4">
          <button
            type="button"
            onClick={() => navigate('dashboard')}
            className={twMerge(
              'cursor-pointer text-left text-base font-bold',
              currentPage.route === 'dashboard' ? 'webui-selsubpoint' : '',
            )}
          >
            Dashboard
          </button>
          <div className="flex flex-col">
            {MENU.filter(
              (group) => isAdmin || group.section !== 'maintenance',
            ).map((group) => {
              const isOpen = sections[group.section];
              const isChildActive = group.items.some(
                (it) => it.id === currentPage.route,
              );
              return (
                <div key={group.section}>
                  <button
                    type="button"
                    className={twMerge(
                      'flex w-full cursor-pointer items-center justify-start border-b border-webui-secondary px-1 py-2 text-left text-sm font-bold text-webui-text hover:bg-webui-secondary',
                      isChildActive
                        ? 'bg-webui-secondary text-webui-primary'
                        : '',
                    )}
                    onClick={() => toggle(group.section)}
                  >
                    <span className="text-sm font-bold">{group.header}</span>
                    <span
                      className={twMerge(
                        'ml-auto inline-block h-0 w-0 border-b-0 border-l-[4px] border-r-[4px] border-t-[6px] border-l-transparent border-r-transparent border-t-webui-muted text-xs text-webui-muted transition-transform duration-200 ease-in-out',
                        isOpen ? 'rotate-180' : '',
                      )}
                    />
                  </button>
                  {sections[group.section] ? (
                    <div className="mt-1 pl-2">
                      <div className="flex flex-col gap-1">
                        {group.items
                          .filter((it) =>
                            it.id === 'settings_poe' || it.id === 'status_poe'
                              ? hasPoe
                              : true,
                          )
                          .map((it) => {
                            const selected = currentPage.route === it.id;
                            return (
                              <button
                                key={it.id}
                                type="button"
                                onClick={() => navigate(it.id)}
                                className={
                                  selected
                                    ? 'webui-selsubpoint text-left'
                                    : 'webui-subpoint text-left'
                                }
                              >
                                {it.label}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className={twMerge(
              'mt-2 cursor-pointer text-left text-base font-bold',
              currentPage.route === 'licenses' ? 'webui-selsubpoint' : '',
            )}
            onClick={() => navigate('licenses')}
          >
            Third Party Notices
          </button>

          <div className="mt-2 flex flex-col gap-1">
            <div className="text-sm font-bold text-webui-muted">
              User:{' '}
              <span className="font-bold text-webui-primary">
                {currentUser?.userName || 'Loading...'}
              </span>
            </div>
            <div className="mt-1">
              <button
                type="button"
                onClick={logout}
                className="cursor-pointer border border-webui-border bg-webui-bg px-2 py-1 text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        <div className="relative h-full w-full flex-1">
          <div className="h-full w-full overflow-auto bg-webui-bg">
            <main className="w-full p-4">{children}</main>
          </div>
        </div>
      </div>
    </div>
  );
}
